import { Button, EmptyState } from '@uroute/ui';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Inbox } from '../components/icons';
import { JobsSkeleton } from '../components/Skeleton';
import { PullToRefresh } from '../components/PullToRefresh';
import { formatRelative, formatTime } from '../lib/format';
import { useNotifications, notificationLink } from '../lib/notifications';

export function NotificationsPage() {
  const navigate = useNavigate();
  const view = useNotifications();

  const open = (id: string, link: string | undefined, readAt: string | null) => {
    if (!readAt) {
      const n = view.items.find((x) => x.id === id);
      if (n) void view.markRead(n);
    }
    if (link) navigate(link);
  };

  return (
    <PullToRefresh onRefresh={() => view.refresh()}>
      <header className="pagehead">
        <div className="pagehead__row">
          <h1 className="pagehead__title">Alerts</h1>
          {view.unread > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void view.markAllRead()}
              data-testid="notifications-mark-all"
            >
              <Check size={14} />
              Mark all read
            </Button>
          ) : null}
        </div>
        {view.unread > 0 ? (
          <p className="pagehead__sub u-data" data-testid="unread-count">
            {view.unread} unread
          </p>
        ) : null}
      </header>

      {view.loading ? (
        <JobsSkeleton count={3} />
      ) : view.items.length === 0 ? (
        <EmptyState
          icon={<Inbox size={40} />}
          title="No alerts"
          description="New assignments and dispatch messages show up here."
        />
      ) : (
        <ul className="notiflist" data-testid="notifications-list">
          {view.items.map((n) => (
            <li
              key={n.id}
              className={`notif${n.readAt ? '' : ' notif--unread'}`}
              data-testid="notification-item"
              data-read={n.readAt ? 'true' : 'false'}
            >
              <button
                type="button"
                className="notif__btn"
                onClick={() => open(n.id, notificationLink(n), n.readAt ?? null)}
              >
                <span className="notif__icon" aria-hidden="true">
                  <Bell size={14} />
                </span>
                <span className="notif__body">
                  <span className="notif__title">{n.title}</span>
                  <span className="notif__text">{n.body}</span>
                  <span className="notif__meta u-data">
                    {formatRelative(n.createdAt)}
                    {n.readAt ? ` · read ${formatTime(n.readAt)}` : ''}
                  </span>
                </span>
                {!n.readAt ? <span className="notif__dot" data-testid="unread-dot" aria-label="Unread" /> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </PullToRefresh>
  );
}
